import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { usePost, useToken } from "../API";
import { AuthContext } from "../Context";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { ApplicantContext } from "./ApplicantContext";
import { ChatConversationContext } from "./ChatContext";

export type ChatSessionContextType = {
  createdAt?: Date,
  isLoading: boolean,
  loaded: boolean,
  termsAccepted?: string,
  setTermsAccepted(termsHash: string): void,
  conversationId?: string,
  hasShownCTA?: boolean,
  clearSession(): void,
  markCTAAsSeen(): void
}

export const ChatSessionContext = React.createContext<ChatSessionContextType>({
  isLoading: false,
  loaded: false,
  setTermsAccepted() { console.warn('no chat session present, ignoring setTermsAccepted()') },
  clearSession() { console.warn('no chat session present, ignoring clearSession()') },
  markCTAAsSeen() { console.warn('no chat session present, ignoring markCTAAsSeen()') }
});

export const ChatSessionProvider: React.FC<{ context: ChatSessionContextType, children: React.ReactNode }> = ({ context, children }) =>
  <ChatSessionContext.Provider value={context}>
    {children}
  </ChatSessionContext.Provider>;

/**
 * Put within any existing ChatContext, to enable persistence of the chat.
 */
export function DefaultChatSessionProvider({ children, conversationIdKey }: { children: React.ReactNode, conversationIdKey?: string }) {
  const conversationIdCookieName = conversationIdKey || "ai_coach_conversation_id";
  const hasShownCTACookieName = "ai_coach_cta_shown";
  const getOrCreateConversation = usePost('/coach/conversation/create', { suppressErrorDisplay: true });
  const { info } = useContext(ApplicantContext);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [createdAt, setCreatedAt] = useState(new Date());
  const [termsAccepted, setTermsAccepted] = useState<string | undefined>();
  const [cookies, setCookie, deleteCookie] = useCookies([conversationIdCookieName]);
  const { dispatch } = useContext(ChatConversationContext);
  const [conversationId, setConversationId] = useState<string | undefined>(cookies[conversationIdCookieName]);
  const authContext = useContext(AuthContext);
  const [, token] = useToken();
  const [hasShownCTA, setHasShownCTA] = useState<boolean>(!!cookies[hasShownCTACookieName]);
  const initialInactivityTimeoutDuration = 1000 * 60 * 2;
  const inactivityTimeoutDuration = useRef<number>(initialInactivityTimeoutDuration);
  
  const clearSession = useCallback(() => {
    deleteCookie(conversationIdCookieName);
    setConversationId(undefined);
    setLoaded(false);
    dispatch({ type: 'clear_chat' });
  }, []);
      
  function markCTAAsSeen() { 
    setCookie(hasShownCTACookieName, Date.now());
    setHasShownCTA(true);
  }

  // Inactivity timeout - show coach CTA again if no target fields change for a given duration
  useEffect(() => {
    const inactivityTimeout = setTimeout(() => {
      // increase timeout
      inactivityTimeoutDuration.current = Math.min(
        inactivityTimeoutDuration.current + initialInactivityTimeoutDuration,
        initialInactivityTimeoutDuration * 10
      );
      setHasShownCTA(false);
    }, inactivityTimeoutDuration.current);
    return () => { 
      clearTimeout(inactivityTimeout);
    }
  }, [info, hasShownCTA]);
  
  // load chat messages
  useAsyncEffect(async (cancel) => {
    if (!loaded && (authContext?.token?.() || token)) {
      setIsLoading(true);
      const existing = await getOrCreateConversation({ previousConvoId: conversationId ?? undefined }, cancel);
      if (existing && !("error" in existing)) {

        if(!conversationId) {
          setConversationId(existing.conversationId);
          setCookie(conversationIdCookieName, existing.conversationId);
        }

        setCreatedAt(new Date(existing.createdAt));

        for (const message of existing.items) {
          if(message.type === 'error') continue;
          if(message.type === 'appState') continue;
          dispatch(message);
        }

        setLoaded(true);
      }
      setIsLoading(false);
    }
  }, [loaded, conversationId, getOrCreateConversation, setConversationId, authContext?.token?.(), token]);

  return <ChatSessionProvider context={{
    createdAt,
    isLoading,
    loaded,
    termsAccepted,
    setTermsAccepted,
    conversationId,
    clearSession,
    hasShownCTA,
    markCTAAsSeen
  }}>
    {children}
  </ChatSessionProvider>
}
